import {
    EventClient,
    EventClientProvider,
    FormattedEvent,
    FormattedEventResponse,
} from '@eb/event-renderer';
import React, { useEffect, useState } from 'react';
import { SettingsContext } from '../../app';
import { AppProps } from '../../types';
import {
    getShareComponentProps,
    ShareProps,
} from '../ShareEvent/ShareEventRoot';
import { dynaMount } from '../utils/mountComponent';
import {
    getOrganizerRelatedEventsFromDestination,
    getRelatedEvents,
} from './api';
import MoreEventsFrom from './MoreEventsFrom';
import OtherEventsYouMayLike from './OtherEventsYouMayLike';
import { useExpiredEvents } from './useExpiredEvents';
import { FORMATTED_RESPONSE_FROM_DESTINATION_COLLECTIONS } from './__mocks__/destinationEventsResponse';
const eventClient = new EventClient();

export interface RelatedEventsProps {
    eventId: string;
    isAuthenticated: boolean;
    shareComponentProps: ShareProps;
    events?: FormattedEvent[];
    organizerId: string;
}

export type RelatedEventsChildrenProps = Omit<
    RelatedEventsProps,
    'organizerId' | 'eventId'
>;

export const RELATED_EVENTS_ID = 'listings-root__related-events';
export const MORE_EVENTS_FROM_ID =
    'listings-root__more-events-from-this-organizer';

export const getRelatedEventsProps = (props: AppProps): RelatedEventsProps => {
    const {
        event: { id: eventId },
        user: { isAuthenticated },
        organizer: { id: organizerId },
    } = props;

    const shareComponentProps = getShareComponentProps(props);

    return {
        eventId,
        isAuthenticated,
        shareComponentProps,
        organizerId,
    };
};

export const OtherEventsYouMayLikeMount = (
    props: RelatedEventsChildrenProps,
): JSX.Element | React.ReactPortal | null =>
    dynaMount(
        {
            Component: OtherEventsYouMayLike,
            containerId: RELATED_EVENTS_ID,
        },
        { ...props },
    );

export const MoreEventsFromMount = (
    props: RelatedEventsChildrenProps,
): JSX.Element | React.ReactPortal | null =>
    dynaMount(
        {
            Component: MoreEventsFrom,
            containerId: MORE_EVENTS_FROM_ID,
        },
        { ...props },
    );

export const RelatedEventsWrapper = (
    props: RelatedEventsProps,
): JSX.Element | null => {
    const {
        featureFlags: {
            launchLinkToCreatorCollectionsOnListing: isCreatorCollectionEvent = false,
        } = {},
    } = React.useContext(SettingsContext);
    const [relatedEvents, setRelatedEvents] = useState<FormattedEvent[]>([]);
    const [relatedEventsFrom, setRelatedEventsFrom] = useState<
        FormattedEvent[]
    >([]);
    const { organizerId, eventId } = props;
    const _getRelatedEventsApi = async (
        eventId: string,
        organizerEvents: string[],
    ) => {
        const dataFromApi = await getRelatedEvents(eventId);
        const filteredEvents =
            organizerEvents.length > 0
                ? dataFromApi.events.filter(
                      (event) => !organizerEvents.includes(event.id),
                  )
                : dataFromApi.events;
        if (filteredEvents.length > 0) {
            setRelatedEvents(filteredEvents);
        }
    };

    useEffect(() => {
        // TODO: We should replace this part to use
        // should_show_related_events_live prop from the mako template instead of a DOM element
        // https://github.com/eventbrite/core/blob/master/django/templates/event/event_page/listing/_listing_footer_related_events.html#L1
        const shouldShowRelatedEventsLive = document.getElementById(
            RELATED_EVENTS_ID,
        );
        let relatedEventsIds: string[] = [];
        const showMoreEventsInThisCollection = () => {
            // TODO: Revisit when we use Creator Collections real data as we are using mocked data
            const creatorCollectionEventsFormatted = FORMATTED_RESPONSE_FROM_DESTINATION_COLLECTIONS.events.slice(
                0,
                3,
            );
            relatedEventsIds = FORMATTED_RESPONSE_FROM_DESTINATION_COLLECTIONS.events.map(
                (event) => event.id,
            );

            setRelatedEventsFrom(creatorCollectionEventsFormatted);

            if (shouldShowRelatedEventsLive) {
                _getRelatedEventsApi(eventId, relatedEventsIds);
            }
        };
        const showMoreEventsFromThisOrganizer = () => {
            getOrganizerRelatedEventsFromDestination(organizerId, eventId).then(
                (response: FormattedEventResponse | undefined) => {
                    if (response) {
                        relatedEventsIds = response.events.map(
                            (event) => event.id,
                        );
                        setRelatedEventsFrom(response.events);
                        if (shouldShowRelatedEventsLive) {
                            _getRelatedEventsApi(eventId, relatedEventsIds);
                        }
                    }
                },
            );
        };

        isCreatorCollectionEvent
            ? showMoreEventsInThisCollection()
            : showMoreEventsFromThisOrganizer();
    }, [eventId, organizerId, isCreatorCollectionEvent]);

    useExpiredEvents();

    return (
        <EventClientProvider client={eventClient}>
            <div data-testid="related-events-wrapper">
                {relatedEventsFrom.length > 0 && (
                    <MoreEventsFromMount
                        {...props}
                        events={relatedEventsFrom}
                    />
                )}

                {relatedEvents.length > 0 && (
                    <OtherEventsYouMayLikeMount
                        {...props}
                        events={relatedEvents}
                    />
                )}
            </div>
        </EventClientProvider>
    );
};

const RelatedEventsRoot = (props: AppProps): JSX.Element | null => {
    // TODO: We should replace this part to use
    // enable_more_events_from_this_organizer_on_listings FF from the mako template
    // https://github.com/eventbrite/core/blob/master/django/templates/event/event_page/listing/_event_listing_body.html#L30
    const enableMoreEventsFrom = document.getElementById(MORE_EVENTS_FROM_ID);

    if (!enableMoreEventsFrom) {
        return <div data-testid="loading-state"></div>;
    }
    const relatedEventsProps = getRelatedEventsProps(props);
    return <RelatedEventsWrapper {...relatedEventsProps} />;
};

export default RelatedEventsRoot;
